// Wave F4 NAMEPLATE, given three honest states by VOCAB-1. Client access to the
// merchant's vocabulary row (public.vocabulary_settings: one row per
// sub-account, a jsonb `words` blob). Surfaces load the merchant's words FROM
// here instead of hardcoding nouns, so the core nouns (and the job-status
// labels) have a single tenant-aware source of truth.
//
// VOCAB-1 — THE THREE STATES. Before this wave a failed read and an account
// that had set nothing produced THE SAME VALUE: DEFAULT_VOCABULARY. So a
// merchant who had renamed "Customers" to "Clients" and whose read failed saw
// "Customers" everywhere with nothing to tell them their words had been asked
// for and not found. Renaming is invisible when it works, which is precisely
// why its failure must not be.
//
// Now: 'loaded' (here are their words, possibly none), or 'failed' (we could
// not ask), and the caller can tell them apart. THE FALLBACK IS STILL THE
// DEFAULTS, deliberately - blank labels would leave a merchant staring at an
// unusable menu. The warning is what makes it honest rather than silent.
//
// A MISSING TABLE IS NOT A FAILURE. On a pre-migration database the table does
// not exist; the honest answer is "this business has configured nothing", and
// that resolves to today's exact words.

import { supabase } from './supabase'
import {
  DEFAULT_VOCABULARY,
  resolveVocabulary,
  resolveVocabularyState,
  type Vocabulary,
  type VocabularyState,
  type VocabularyWords,
} from './vocabulary'

// Cache keyed by sub-account id. A resolved state is immutable per load.
const cache = new Map<string, VocabularyState>()
const inflight = new Map<string, Promise<VocabularyState>>()

/** Postgres/PostgREST codes meaning "that table is not there yet". The ONLY
 *  error treated as an answer rather than a failure. */
const TABLE_ABSENT = new Set(['42P01', 'PGRST205'])

/**
 * Load and cache the vocabulary STATE for a sub-account. Called without an id
 * (or before auth resolves) it reports 'loading'. Concurrent callers share one
 * request per tenant.
 *
 * A FAILURE IS NEVER CACHED, so the next call retries.
 */
export async function loadVocabularyState(subAccountId?: string | null): Promise<VocabularyState> {
  if (!subAccountId) return { state: 'loading' }
  const hit = cache.get(subAccountId)
  if (hit) return hit
  const pending = inflight.get(subAccountId)
  if (pending) return pending
  const promise = (async (): Promise<VocabularyState> => {
    try {
      // Boundary cast: vocabulary_settings is not in the generated types yet.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any
      const res = await sb
        .from('vocabulary_settings')
        .select('words')
        .eq('sub_account_id', subAccountId)
        .maybeSingle()
      if (res.error) {
        if (TABLE_ABSENT.has(res.error.code)) {
          const answer: VocabularyState = { state: 'loaded', words: null }
          cache.set(subAccountId, answer)
          return answer
        }
        return {
          state: 'failed',
          reason: res.error.message ?? 'The read did not succeed.',
        }
      }
      const row = res.data as { words?: VocabularyWords | null } | null
      const answer: VocabularyState = { state: 'loaded', words: row?.words ?? null }
      cache.set(subAccountId, answer)
      return answer
    } catch (e) {
      return {
        state: 'failed',
        reason: e instanceof Error ? e.message : 'The read did not succeed.',
      }
    } finally {
      inflight.delete(subAccountId)
    }
  })()
  inflight.set(subAccountId, promise)
  return promise
}

/**
 * The resolved words alone, for the callers that cannot act on a failure
 * anyway (the site builder's default copy, the portal payload).
 *
 * KEPT SO THE THREE STATES ARE A WIDENING AND NOT A BREAKING CHANGE: every F4
 * caller still compiles, and each one moves to loadVocabularyState when it has
 * somewhere to put a warning.
 */
export async function loadVocabulary(subAccountId?: string | null): Promise<Vocabulary> {
  if (!subAccountId) return DEFAULT_VOCABULARY
  return resolveVocabularyState(await loadVocabularyState(subAccountId)).vocab
}

/** Test / post-save seam: drop every cached tenant's vocabulary. */
export function resetVocabularyCache(): void {
  cache.clear()
  inflight.clear()
}

// ── Manager (settings editor) ───────────────────────────────────────────────

/** Load the RAW stored words for the settings editor (NOT merged with the
 *  defaults — the editor shows the defaults as placeholders and only what the
 *  merchant actually overrode as values). Returns {} when there is no row. */
export async function loadVocabularyWordsForManage(subAccountId: string): Promise<VocabularyWords> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('vocabulary_settings')
    .select('words')
    .eq('sub_account_id', subAccountId)
    .maybeSingle()
  if (res.error) throw new Error(res.error.message)
  const words = (res.data as { words?: VocabularyWords } | null)?.words
  return words && typeof words === 'object' ? words : {}
}

/** Upsert the merchant's words blob (one row per sub-account) and drop the
 *  cache so the next read reflects the change. RLS requires a role holding
 *  settings management — see the VOCAB-1 migration, which tightens this from
 *  "any accepted member". */
export async function saveVocabularyWords(
  subAccountId: string,
  words: VocabularyWords,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('vocabulary_settings')
    .upsert({ sub_account_id: subAccountId, words }, { onConflict: 'sub_account_id' })
  if (res.error) throw new Error(res.error.message)
  resetVocabularyCache()
}

/** Re-exported so a caller needing the pure merge does not reach past this
 *  module into the lib. */
export { resolveVocabulary }
